// src/components/AppShowcase.js — Light Theme
import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import Lottie from "lottie-react";

const AppShowcase = () => {
  const [animationData, setAnimationData] = React.useState(null);

  React.useEffect(() => {
    fetch("/animations/Robot-Bot 3D.json")
      .then((res) => res.json())
      .then((data) => setAnimationData(data))
      .catch((err) => console.error("Error loading Lottie JSON:", err));
  }, []);

  return (
    <Section id="showcase">
      <BgAccent />

      <LeftLottieContainer
        initial={{ opacity: 0, x: -25 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        {animationData && (
          <Lottie animationData={animationData} loop className="lottie" />
        )}
        {!animationData && <LottiePlaceholder />}
      </LeftLottieContainer>

      <RightContent
        initial={{ opacity: 0, x: 25 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <SectionBadge>AI Crypto Signals App</SectionBadge>

        <Title>
          Smarter Crypto Trading Starts with{" "}
          <GradientText>Trend Genie</GradientText>
        </Title>

        <Subtitle>
          The best free crypto signals app for Android. Get real-time AI-driven signals,
          personalized watchlists, and a clean interface designed to help you trade
          confidently — whether you are a beginner or a pro.
        </Subtitle>

        <BulletList>
          <BulletItem>
            <BulletDot />
            <span>Real-time AI crypto signals with entry, TP &amp; SL levels</span>
          </BulletItem>
          <BulletItem>
            <BulletDot />
            <span>Personalized watchlists &amp; portfolio tracking</span>
          </BulletItem>
          <BulletItem>
            <BulletDot />
            <span>Clean, fast, beginner-friendly Android interface</span>
          </BulletItem>
          <BulletItem>
            <BulletDot />
            <span>Free to download — best crypto project for 2025</span>
          </BulletItem>
        </BulletList>

        <CTAButtons>
          <PrimaryCTA
            href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Free on Android
          </PrimaryCTA>

          <SecondaryCTA href="#features">Explore Features</SecondaryCTA>
        </CTAButtons>
      </RightContent>
    </Section>
  );
};

export default AppShowcase;

/* ---- STYLES ---- */

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const Section = styled.section`
  padding: 7rem 10%;
  background: var(--bg-main);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    gap: 2.5rem;
  }
`;

const BgAccent = styled.div`
  position: absolute;
  right: -100px;
  top: -100px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(247, 148, 29, 0.07), transparent 70%);
  pointer-events: none;
  z-index: 0;
`;

const LeftLottieContainer = styled(motion.div)`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;

  .lottie {
    width: 400px;
    max-width: 100%;
    filter: drop-shadow(0 20px 40px rgba(41, 171, 226, 0.15));
  }

  @media (max-width: 768px) {
    .lottie {
      width: 280px;
    }
  }
`;

const LottiePlaceholder = styled.div`
  width: 380px;
  height: 380px;
  border-radius: 24px;
  background: var(--bg-secondary);
  border: 2px dashed var(--border-color);
  animation: ${pulse} 3s ease-in-out infinite;
`;

const RightContent = styled(motion.div)`
  flex: 1;
  z-index: 1;
`;

const SectionBadge = styled.span`
  display: inline-block;
  background: var(--accent-light);
  color: var(--accent-color);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: 2.6rem;
  font-weight: 800;
  color: var(--text-color);
  margin-bottom: 1.2rem;
  line-height: 1.2;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: var(--text-secondary-color);
  max-width: 500px;
  margin-bottom: 2rem;
  line-height: 1.75;

  @media (max-width: 992px) {
    max-width: 100%;
  }
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.97rem;
  color: var(--text-secondary-color);
  line-height: 1.5;

  @media (max-width: 992px) {
    justify-content: center;
    text-align: left;
  }
`;

const BulletDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  flex-shrink: 0;
  margin-top: 6px;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const PrimaryCTA = styled.a`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  padding: 0.95rem 2rem;
  border-radius: 14px;
  font-weight: 700;
  font-size: 1rem;
  color: white;
  text-decoration: none;
  box-shadow: var(--shadow-primary);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-primary-lg);
  }
`;

const SecondaryCTA = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.95rem 1.8rem;
  border-radius: 14px;
  color: var(--text-secondary-color);
  border: 1.5px solid var(--border-color);
  background: var(--bg-surface);
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;
